/** @typedef {import("../../api/types").Showcase} ShowcaseData */
import React, {useEffect, useState} from "react";
import axios from "axios";
import {useParams} from "react-router-dom";
import {Alert, Card, CardBody, CardHeader, Container, Spinner} from "reactstrap";
import AddressLink from "../../shared/components/AddressLink";
import Field from "../../shared/components/Field";
import IconLabel from "../../shared/components/IconLabel";
import {ChatIcon, ShopIcon} from "../../shared/components/icons";
import PhoneLink from "../../shared/components/PhoneLink";
import {useProviderTheme} from "../../hooks/useProviderTheme";
import {formatTime} from "../../shared/utils/format";

/**
 * A provider's public page, so clients can reach out. No login: it is shown at
 * /p/<showcase name> and reads the public showcase endpoint.
 *
 * Only what the provider has filled in is shown; a public page has no use for
 * "Sem informação".
 */
const Showcase = () => {
    const {showcaseName} = useParams();
    const [showcase, setShowcase] = useState(/** @type {ShowcaseData|null} */ (null));
    const [status, setStatus] = useState(/** @type {"loading"|"ready"|"missing"|"error"} */ ("loading"));

    useEffect(() => {
        const controller = new AbortController();
        setStatus("loading");
        setShowcase(null);

        axios
            .get(`/os/api/showcase/${encodeURIComponent(showcaseName)}/`, {signal: controller.signal})
            .then(({data}) => {
                setShowcase(data);
                setStatus("ready");
            })
            .catch((err) => {
                if (axios.isCancel(err)) return;
                setStatus(err.response?.status === 404 ? "missing" : "error");
            });

        return () => controller.abort();
    }, [showcaseName]);

    useProviderTheme(showcase ? showcase.theme : undefined);

    // The tab shows who this is, like any business page.
    useEffect(() => {
        if (!showcase) return undefined;
        const previous = document.title;
        document.title = showcase.business_name;
        return () => {
            document.title = previous;
        };
    }, [showcase]);

    if (status === "loading") {
        return (
            <div className="text-center mt-5">
                <Spinner />
            </div>
        );
    }

    if (status !== "ready" || !showcase) {
        return (
            <Container className="mt-5 text-center" style={{maxWidth: 480}}>
                {status === "missing" ? (
                    <>
                        <h3>Página não encontrada</h3>
                        <p className="text-muted">O endereço pode estar incorreto ou a página não existe mais.</p>
                    </>
                ) : (
                    <Alert color="danger">Não foi possível carregar a página. Tente novamente.</Alert>
                )}
            </Container>
        );
    }

    const hours =
        showcase.opening_time && showcase.closing_time
            ? `${formatTime(showcase.opening_time)} às ${formatTime(showcase.closing_time)}`
            : "";
    // Each row has its text (what the Field checks) and, where it differs, how to show it.
    const contact = [
        {label: "Telefone", value: showcase.phone, node: <PhoneLink phone={showcase.phone} />},
        {
            label: "Endereço",
            value: showcase.address,
            node: <AddressLink address={showcase.address} area={showcase.city} />,
        },
        {label: "Cidade", value: showcase.city},
        {label: "Horário de atendimento", value: hours},
    ].filter((row) => row.value?.trim());

    return (
        <Container className="mt-5 mb-5" style={{maxWidth: 720}}>
            <header className="mb-4">
                <h1 className="h2 mb-1">{showcase.business_name}</h1>
                {showcase.owner_name && <div className="text-muted">Responsável: {showcase.owner_name}</div>}
            </header>

            {showcase.about?.trim() && (
                <Card className="mb-3">
                    <CardHeader>
                        <IconLabel icon={<ShopIcon />}>Sobre</IconLabel>
                    </CardHeader>
                    <CardBody style={{whiteSpace: "pre-wrap"}}>{showcase.about}</CardBody>
                </Card>
            )}

            {contact.length > 0 && (
                <Card className="mb-3">
                    <CardHeader>
                        <IconLabel icon={<ChatIcon />}>Contato</IconLabel>
                    </CardHeader>
                    <CardBody>
                        <div className="row g-3">
                            {contact.map(({label, value, node}) => (
                                <div className="col-12 col-md-6" key={label}>
                                    <Field label={label} value={value}>
                                        {node}
                                    </Field>
                                </div>
                            ))}
                        </div>
                    </CardBody>
                </Card>
            )}
        </Container>
    );
};

export default Showcase;
